/**
 * Satu konteks WebGL2 untuk SELURUH halaman — bukan satu per instance orb.
 *
 * Kenapa: FAB dan header bisa tampil bersamaan, dan halaman admin bisa
 * menampilkan 6 kartu varian + pratinjau sekaligus. Membuat satu konteks per
 * instance pernah dicoba di prototipe (_refs/jenna-orb/prototype/
 * orb-liquid-glass-prototype.html) dan terbukti membuat browser meng-evict
 * konteks lama (`isContextLost()` jadi true) begitu jumlahnya banyak.
 *
 * Tiap orb WebGL jadi "sprite": satu kanvas overlay `position:fixed`
 * penuh-viewport, dan tiap instance digambar lewat `gl.viewport()` +
 * `gl.scissor()` ke posisi elemen aslinya di layar (diukur ulang tiap frame
 * lewat `getBoundingClientRect()`, jadi otomatis ikut saat halaman di-scroll).
 * Logika gambar-per-frame-nya sendiri ada di orb-webgl-draw.ts; berkas ini
 * hanya memegang siklus hidup konteks (buat/gagal/hilang), program per jenis
 * shader, dan pendaftaran.
 *
 * Fallback: kalau `getContext("webgl2")` gagal, atau event `webglcontextlost`
 * terjadi, SEMUA jenis jadi tidak tersedia PERMANEN untuk sesi ini (sengaja
 * tidak mencoba memulihkan konteks — logika restore rawan keliru untuk kasus
 * yang sudah jarang terjadi). Kalau hanya SATU program gagal dikompilasi, hanya
 * jenis itu yang jatuh; jenis lain tetap jalan. Pelanggan diberi tahu lewat
 * `subscribeToWebglAvailability`, dan AssistantOrb beralih merender varian
 * Canvas 2D (glow-ring) sebagai gantinya — lihat assistant-orb.tsx.
 *
 * Konteks dibuat `premultipliedAlpha:true` dan di-blend (ONE, ONE_MINUS_SRC_ALPHA):
 * SEMUA shader di sini wajib mengeluarkan warna premultiplied.
 */

import { ORB_WEBGL_STAGE_Z_INDEX } from "@/components/assistant/orb/orb.config";
import { subscribeToOrbLoop } from "@/components/assistant/orb/orb-loop";
import { type DrawContext, drawWebglStageFrame } from "./orb-webgl-draw";
import { buildProgramInfo, type ProgramInfo } from "./orb-webgl-programs";
import type {
	WebglOrbInstanceState,
	WebglShaderKind,
} from "./orb-webgl-stage.types";

export {
	type WebglOrbInstanceData,
	type WebglOrbInstanceState,
	type WebglOrbRect,
	type WebglShaderKind,
	webglShaderKindFor,
} from "./orb-webgl-stage.types";

type AvailabilityListener = (available: boolean) => void;

const DEFAULT_KIND: WebglShaderKind = "liquid-glass";

let canvas: HTMLCanvasElement | null = null;
let gl: WebGL2RenderingContext | null = null;
let contextOk = true;
let attemptedInit = false;
let unsubscribeFromOrbLoop: (() => void) | null = null;

const programs = new Map<WebglShaderKind, ProgramInfo>();
const failedKinds = new Set<WebglShaderKind>();
const instances = new Set<() => WebglOrbInstanceState>();
const listeners = new Map<AvailabilityListener, WebglShaderKind>();

const availableFor = (kind: WebglShaderKind): boolean =>
	contextOk && !failedKinds.has(kind);

function notifyListeners(): void {
	for (const [listener, kind] of listeners) listener(availableFor(kind));
}

function failContext(): void {
	if (!contextOk) return;
	contextOk = false;
	notifyListeners();
}

/** Buat kanvas overlay + konteks sekali saja, saat instance pertama mendaftar. */
function ensureContext(): boolean {
	if (attemptedInit) return contextOk;
	attemptedInit = true;

	canvas = document.createElement("canvas");
	canvas.style.position = "fixed";
	canvas.style.inset = "0";
	canvas.style.width = "100%";
	canvas.style.height = "100%";
	canvas.style.pointerEvents = "none";
	canvas.style.zIndex = String(ORB_WEBGL_STAGE_Z_INDEX);
	document.body.appendChild(canvas);

	try {
		// preserveDrawingBuffer:true — tanpa ini browser boleh membersihkan
		// buffer sendiri segera setelah tiap komposit; dipelajari lewat bug
		// nyata di prototipe (readback tampak kosong walau shader benar).
		gl = canvas.getContext("webgl2", {
			alpha: true,
			antialias: true,
			// premultipliedAlpha:true + keluaran shader premultiplied: dengan alpha
			// "straight", kuantisasi alpha 8-bit di area redup menggeser rona dan
			// menimbulkan pita warna (dibuktikan di prototipe liquid-morph).
			premultipliedAlpha: true,
			preserveDrawingBuffer: true,
		});
	} catch {
		gl = null;
	}

	if (!gl) {
		failContext();
		return false;
	}

	canvas.addEventListener(
		"webglcontextlost",
		(event) => {
			event.preventDefault();
			failContext();
		},
		{ passive: false },
	);

	gl.bindVertexArray(gl.createVertexArray());
	gl.enable(gl.BLEND);
	gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA); // sumber premultiplied
	gl.enable(gl.SCISSOR_TEST);
	gl.clearColor(0, 0, 0, 0);
	return true;
}

/** Kompilasi program sebuah jenis (sekali). Gagal → hanya jenis itu yang jatuh ke fallback. */
function ensureProgram(kind: WebglShaderKind): boolean {
	if (!gl || !contextOk) return false;
	if (programs.has(kind)) return true;
	if (failedKinds.has(kind)) return false;
	try {
		programs.set(kind, buildProgramInfo(gl, kind));
		return true;
	} catch {
		failedKinds.add(kind);
		notifyListeners();
		return false;
	}
}

function drawFrame(elapsedMs: number): void {
	if (!gl || !canvas) return;
	const ctx: DrawContext = { gl, canvas, programs, instances };
	drawWebglStageFrame(ctx, elapsedMs);
}

/**
 * Daftarkan satu orb WebGL ke panggung bersama. `getState` dipanggil TIAP
 * FRAME (bukan sekali), jadi posisi/warna/parameter boleh berubah tanpa
 * mendaftar ulang — sama seperti pola `frameRef` pada varian Canvas 2D.
 * Mengembalikan fungsi pembatalan.
 */
export function registerWebglOrbInstance(
	getState: () => WebglOrbInstanceState,
): () => void {
	const kind = getState().kind;
	const ok = ensureContext() && ensureProgram(kind);
	instances.add(getState);
	if (ok && !unsubscribeFromOrbLoop) {
		// Satu subscriber mewakili SELURUH panggung WebGL di loop rAF bersama —
		// bukan satu subscriber per instance. fps 60 karena sprite WebGL
		// hanya hidup saat ukurannya besar (lihat targetFps di orb-motion.ts
		// untuk pola serupa); biaya gambar WebGL terukur <1ms per orb di M1,
		// jadi 60fps aman.
		unsubscribeFromOrbLoop = subscribeToOrbLoop({ fps: 60, draw: drawFrame });
	}
	return () => {
		instances.delete(getState);
		if (instances.size === 0 && unsubscribeFromOrbLoop) {
			unsubscribeFromOrbLoop();
			unsubscribeFromOrbLoop = null;
		}
	};
}

/** true bila jenis shader ini bisa dipakai SEKARANG — belum tentu final sebelum instance pertama mendaftar. */
export function isWebglStageAvailable(
	kind: WebglShaderKind = DEFAULT_KIND,
): boolean {
	return availableFor(kind);
}

/** Dipanggil lagi setiap kali ketersediaan berubah (termasuk context loss di tengah sesi). */
export function subscribeToWebglAvailability(
	listener: AvailabilityListener,
	kind: WebglShaderKind = DEFAULT_KIND,
): () => void {
	listeners.set(listener, kind);
	return () => {
		listeners.delete(listener);
	};
}

/** Jumlah instance terdaftar — dipakai test, bukan kode produksi. */
export function webglOrbInstanceCount(): number {
	return instances.size;
}

/** Reset total untuk test — melepas kanvas & konteks, mengembalikan ke keadaan awal. */
export function resetWebglStageForTest(): void {
	unsubscribeFromOrbLoop?.();
	unsubscribeFromOrbLoop = null;
	instances.clear();
	listeners.clear();
	programs.clear();
	failedKinds.clear();
	canvas?.remove();
	canvas = null;
	gl = null;
	contextOk = true;
	attemptedInit = false;
}
